import { syncBisModalVariant } from '@theme/back-in-stock';
import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, queryCheckedRadioByVariantId } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Plays add-to-cart fly + button state animations when the theme setting is on.
   * Use when the form is submitted without a real button click (e.g. product card option-stack auto-add).
   * @param {Element} [sourceForModalCheck] - Element for `.closest('.quick-add-modal')` (modal uses main PDP animation only)
   * @param {Element} [flySourceElement] - When the add button is not laid out (e.g. `display: none`), use this for fly start position
   */
  playAddToCartAnimations(sourceForModalCheck, flySourceElement) {
    if (this.refs.addToCartButton.dataset.puppet === 'true') {
      return;
    }
    const source = sourceForModalCheck ?? this;
    const animationEnabled = this.dataset.addToCartAnimation === 'true';
    if (animationEnabled && !source?.closest?.('.quick-add-modal')) {
      this.#animateFlyToCart(flySourceElement);
    }
    this.animateAddToCart();
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    this.playAddToCartAnimations(event.target);
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   * @param {Element} [flySourceElement] - Visible element to measure when the add button has no layout (hidden quick-add)
   */
  #animateFlyToCart(flySourceElement) {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const buttonRect = addToCartButton.getBoundingClientRect();
    const useOverride =
      flySourceElement instanceof Element &&
      document.contains(flySourceElement) &&
      (buttonRect.width < 1 || buttonRect.height < 1);
    const flySource = useOverride ? flySourceElement : addToCartButton;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = flySource;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async () => {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    const resetTimeouts = /** @type {number[]} */ (this.#resetTimeouts);
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      const index = resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        resetTimeouts.splice(index, 1);
      }
    }, 800);

    resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #syncVariantIdFromProductCardForInlineQuickAdd() {
    const quickAdd = this.closest('quick-add-component');
    if (!(quickAdd instanceof HTMLElement) || quickAdd.dataset.inlineQuickAdd !== 'true') {
      return;
    }
    const card = this.closest('product-card');
    if (!card) {
      return;
    }
    const getId =
      'getSelectedVariantId' in card && typeof card.getSelectedVariantId === 'function'
        ? () => (/** @type {import('./product-card').ProductCard} */ (card)).getSelectedVariantId()
        : null;
    if (!getId) {
      return;
    }
    const selected = getId();
    if (!selected) {
      return;
    }
    const vInput = this.refs.variantId;
    if (!(vInput instanceof HTMLInputElement)) {
      return;
    }
    const selectedRadio = queryCheckedRadioByVariantId(card, selected);
    const isUnavailable =
      selectedRadio?.dataset.optionAvailable === 'false' ||
      selectedRadio?.getAttribute('aria-disabled') === 'true';

    vInput.value = selected;
    vInput.disabled = Boolean(isUnavailable);
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartQty = cart.items
      .filter((item) => item.variant_id.toString() === variantIdInput.value.toString())
      .reduce((sum, item) => sum + item.quantity, 0);

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {SubmitEvent} event - The submit event.
   */
  handleSubmit(event) {
    this.#syncVariantIdFromProductCardForInlineQuickAdd();

    const { addToCartTextError } = this.refs;
    const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : null;
    const atcFromSubmit = submitter?.closest('add-to-cart-component');

    event.preventDefault();

    if (this.refs.variantId instanceof HTMLInputElement && this.refs.variantId.disabled) {
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    const variantIdInput = this.refs.variantId;
    const stackProgrammaticAdd =
      !submitter &&
      variantIdInput instanceof HTMLInputElement &&
      Boolean(variantIdInput.value) &&
      !variantIdInput.disabled &&
      Boolean(this.closest('product-card')?.querySelector('.options-block--opt-stack'));

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled && !stackProgrammaticAdd) return;

    if (atcFromSubmit instanceof AddToCartComponent && atcFromSubmit.refs.addToCartButton?.dataset.added !== 'true') {
      atcFromSubmit.playAddToCartAnimations(submitter ?? undefined);
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    formData.append('properties[_addedAt]', String(Date.now()));

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Anchor element when adding a new node
   * @param {'beforebegin' | 'afterend'} [insertPosition] - Where to place a new node relative to the anchor
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null,
    insertPosition = 'beforebegin'
  ) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      const nodeToInsert =
        newElement.ownerDocument === document
          ? /** @type {Element} */ (newElement)
          : document.adoptNode(/** @type {Element} */ (newElement));
      insertReferenceElement.insertAdjacentElement(insertPosition, nodeToInsert);
    }
  }

  /**
   * @param {Element | null | undefined} bisModal
   * @param {string | number | null | undefined} [variantId]
   */
  #openBisModal(bisModal, variantId) {
    if (!(bisModal instanceof Element)) return;

    const dialog = bisModal.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      if (dialog.open) {
        try {
          dialog.close();
        } catch (error) {
          console.warn('back-in-stock-modal dialog close failed before open', error);
        }
      }
      dialog.open = false;
      dialog.classList.remove('dialog-closing');
    }

    if (variantId != null && variantId !== '') {
      syncBisModalVariant(bisModal, variantId);
    }

    if (bisModal instanceof Component) {
      try {
        bisModal.updatedCallback();
      } catch (error) {
        console.warn('back-in-stock-modal failed to refresh refs before open', error);
      }
    }

    const dialogHost = /** @type {Element & { showDialog?: () => void }} */ (bisModal);
    if (typeof dialogHost.showDialog === 'function') {
      try {
        dialogHost.showDialog();
        return;
      } catch (error) {
        console.warn('back-in-stock-modal showDialog failed', error);
      }
    }

    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      dialog.showModal();
    }
  }

  /**
   * Prefer server-rendered buy-buttons markup over picker JSON, which falls back to the first
   * available variant when the shopper selected a sold-out option.
   *
   * @param {HTMLElement | null} newProductForm
   * @param {import('@theme/events').VariantUpdateEvent['detail']['resource']} fallbackResource
   * @returns {{ id: string, available: boolean }}
   */
  #resolveVariantFromVariantUpdateHtml(newProductForm, fallbackResource) {
    if (newProductForm instanceof HTMLElement) {
      const notifyMe = newProductForm.querySelector('.notify-me-button');
      if (notifyMe instanceof HTMLButtonElement && notifyMe.dataset.variantId) {
        return { id: notifyMe.dataset.variantId, available: false };
      }

      const variantInput = newProductForm.querySelector('[ref="variantId"]');
      if (variantInput instanceof HTMLInputElement && variantInput.value) {
        const addToCartButton = newProductForm.querySelector('[ref="addToCartButton"]');
        const addToCartUnavailable =
          addToCartButton instanceof HTMLButtonElement && addToCartButton.disabled;
        return {
          id: variantInput.value,
          available: !addToCartUnavailable,
        };
      }
    }

    return {
      id: fallbackResource?.id != null ? String(fallbackResource.id) : '',
      available: fallbackResource != null && fallbackResource.available !== false,
    };
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    const incomingProductId = event.detail.data.newProduct?.id ?? event.detail.data.productId ?? null;
    if (incomingProductId) {
      this.dataset.productId = String(incomingProductId);
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const resource = event.detail.resource;
    const html = event.detail.data.html;
    const quickAddModal = this.closest('quick-add-dialog, .quick-add-modal');

    let newProductForm = null;
    if (html) {
      const productId = this.dataset.productId;
      if (productId) {
        const esc = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(productId) : productId;
        newProductForm = html.querySelector(`product-form-component[data-product-id="${esc}"]`);
      }
      if (quickAddModal && !(newProductForm instanceof HTMLElement)) {
        newProductForm =
          html.querySelector('[data-product-grid-content] product-form-component') ??
          html.querySelector('.product-details product-form-component');
      }
      if (!(newProductForm instanceof HTMLElement)) {
        newProductForm = html.querySelector('product-form-component');
      }
    }

    const resolvedVariant = this.#resolveVariantFromVariantUpdateHtml(
      newProductForm instanceof HTMLElement ? newProductForm : null,
      resource
    );

    variantId.value = resolvedVariant.id;
    variantId.disabled = !resolvedVariant.available;

    const isAvailable = resolvedVariant.available;
    if (addToCartButtonContainer) {
      if (isAvailable) {
        addToCartButtonContainer.enable();
      } else {
        addToCartButtonContainer.disable();
      }
    }
    if (acceleratedCheckoutButtonContainer) {
      if (isAvailable) {
        acceleratedCheckoutButtonContainer.removeAttribute('hidden');
      } else {
        acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
      }
    }

    const buyButtonsBlock = this.closest('.buy-buttons-block');

    if (quickAddModal && buyButtonsBlock?.classList.contains('buy-buttons-block--floating')) {
      return;
    }

    const buyButtonsClass = buyButtonsBlock
      ? [...buyButtonsBlock.classList].find((c) => c.startsWith('buy-buttons-block--'))
      : null;
    const blockId = buyButtonsClass?.slice('buy-buttons-block--'.length);
    const productFormButtonsSelector =
      blockId && typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? `.buy-buttons-block--${CSS.escape(blockId)} .product-form-buttons`
        : null;

    if (newProductForm instanceof HTMLElement) {
      if (newProductForm.dataset.productUrl) {
        this.dataset.productUrl = newProductForm.dataset.productUrl;
      }

      const productFormButtons =
        buyButtonsBlock?.querySelector('.product-form-buttons') ?? this.querySelector('.product-form-buttons');
      let newProductFormButtons = productFormButtonsSelector ? html?.querySelector(productFormButtonsSelector) : null;
      if (!newProductFormButtons) {
        newProductFormButtons = newProductForm.querySelector('.product-form-buttons');
      }

      const currentNotifyMe = this.querySelector('.notify-me-button');
      const newNotifyMe = newProductForm.querySelector('.notify-me-button');
      const purchaseTypeChanging =
        Boolean(currentNotifyMe) !== Boolean(newNotifyMe) ||
        Boolean(this.refs.addToCartButtonContainer) !== Boolean(newProductForm.querySelector('[ref="addToCartButtonContainer"]'));

      if (purchaseTypeChanging && productFormButtons && newProductFormButtons) {
        try {
          morph(productFormButtons, newProductFormButtons);
        } catch {
          // ignore morph errors
        }
      } else if (currentNotifyMe && newNotifyMe) {
        morph(currentNotifyMe, newNotifyMe);
      } else {
        const currentAddToCartButton = this.refs.addToCartButtonContainer?.refs?.addToCartButton;
        const newAddToCartButton = newProductForm.querySelector('[ref="addToCartButton"]');
        if (currentAddToCartButton && newAddToCartButton) {
          morph(currentAddToCartButton, newAddToCartButton);
        }
      }

      if (!event.detail.data.newProduct) {
        this.#morphOrUpdateElement(
          this.querySelector('back-in-stock-modal'),
          newProductForm.querySelector('back-in-stock-modal'),
          this.querySelector('form'),
          'afterend'
        );
      }

      if (resolvedVariant.id) {
        const buyButtonsBlockForBis = this.closest('.buy-buttons-block');
        const bisModal =
          buyButtonsBlockForBis?.querySelector('back-in-stock-modal') ??
          this.querySelector('back-in-stock-modal');
        syncBisModalVariant(bisModal, resolvedVariant.id);
        if (bisModal instanceof Component) {
          try {
            bisModal.updatedCallback();
          } catch (error) {
            console.warn('back-in-stock-modal failed to refresh refs after variant update', error);
          }
        }
      }

      if (resource && String(resource.id) === resolvedVariant.id) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        const atcContainer = this.refs.addToCartButtonContainer;
        if (productVariantMedia && atcContainer) {
          atcContainer.setAttribute('data-product-variant-media', `${productVariantMedia}&width=100`);
        }
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      let newProductFormButtons = productFormButtonsSelector
        ? event.detail.data.html.querySelector(productFormButtonsSelector)
        : null;
      if (!newProductFormButtons && newProductForm instanceof HTMLElement) {
        newProductFormButtons = newProductForm.querySelector('.product-form-buttons');
      }

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };

  handleNotifyMeClick = (event) => {
    const notifyMeButton =
      event.target instanceof Element ? event.target.closest('.notify-me-button') : null;
    if (!(notifyMeButton instanceof HTMLButtonElement)) return;

    event.preventDefault();

    const quickAddModal = this.closest('quick-add-dialog, .quick-add-modal');
    const resolvedVariantId =
      this.refs.variantId?.value || notifyMeButton.dataset.variantId || '';

    if (quickAddModal) {
      const modalPicker = /** @type {HTMLElement | null} */ (
        this.closest('#quick-add-modal-content')?.querySelector('variant-picker')
      );
      const productUrl =
        modalPicker?.dataset.productUrl ||
        this.dataset.productUrl ||
        '';

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      if (resolvedVariantId) {
        url.searchParams.set('variant', resolvedVariantId);
      }
      url.searchParams.set('bis', '1');
      window.location.href = url.toString();
      return;
    }

    const buyButtonsBlock = notifyMeButton.closest('.buy-buttons-block');
    const bisModal = buyButtonsBlock?.querySelector('back-in-stock-modal');
    if (!(bisModal instanceof Element)) return;

    this.#openBisModal(bisModal, resolvedVariantId);
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
